import type { TableView } from 'owa-mail-list-store';
import { SelectionDirection, getListViewState } from 'owa-mail-list-store';
import {
    getMailListGroupHeader,
    getMailListGroupHeaderGenerator,
    getParentGroupHeader,
    virtualizedMailGroupHeadersStore,
} from 'owa-virtualized-group-headers';
import { isImmersiveReadingPaneShown } from 'owa-mail-layout';

interface NavigationResult {
    selectedIndex: number;
    headerToFocus: string | null;
}

interface NavigationContext {
    tableView: TableView;
    currentSelectedIndex: number;
    visibleStartIndex: number;
    visibleEndIndex: number;
    numVisibleRows: number;
    totalRowsCount: number;
}

/**
 * Get the index of the next row index to take action on (focus or selection)
 * @param tableView where the operation is being performed
 * @param selectionDirection direction of the selection
 * @param shouldConsiderHeaders whether to consider group headers for navigation (false for focus-only operations like Ctrl+Up/Down)
 * @return the next index to take action on and whether to focus on the header
 *         or not. If no valid index is found, returns -1 for selectedIndex and
 *         null for headerToFocus.
 */
export default function getNextRowIndexInternal(
    tableView: TableView,
    selectionDirection: SelectionDirection,
    shouldConsiderHeaders: boolean = true
): NavigationResult {
    // Early validation
    if (!isValidTableState(tableView)) {
        return { selectedIndex: -1, headerToFocus: null };
    }

    const context = createNavigationContext(tableView);
    if (context.currentSelectedIndex === -1) {
        return { selectedIndex: -1, headerToFocus: null };
    }

    return getNextRowByDirection(context, selectionDirection, shouldConsiderHeaders);
}

/**
 * Validates if the table state is valid for navigation
 */
function isValidTableState(tableView: TableView): boolean {
    const selectedRowKeys = [...tableView.selectedRowKeys.keys()];

    return (
        tableView.rowKeys.length > 0 &&
        (selectedRowKeys.length > 0 ||
            !!tableView.selectionAnchorRowKey ||
            !!tableView.focusedRowKey)
    );
}

/**
 * Creates navigation context with all necessary state
 */
function createNavigationContext(tableView: TableView): NavigationContext {
    const rowKey = tableView.selectionAnchorRowKey || tableView.focusedRowKey;
    const currentSelectedIndex = rowKey ? tableView.rowKeys.indexOf(rowKey) : -1;

    const listViewState = getListViewState();
    const visibleStartIndex = listViewState.startIndexVLV;
    const visibleEndIndex = listViewState.endIndexVLV;
    const numVisibleRows = visibleEndIndex - visibleStartIndex + 1;
    const totalRowsCount = tableView.rowKeys.length;

    return {
        tableView,
        currentSelectedIndex,
        visibleStartIndex,
        visibleEndIndex,
        numVisibleRows,
        totalRowsCount,
    };
}

/**
 * Routes to the appropriate navigation handler based on direction
 */
function getNextRowByDirection(
    context: NavigationContext,
    selectionDirection: SelectionDirection,
    shouldConsiderHeaders: boolean
): NavigationResult {
    switch (selectionDirection) {
        case SelectionDirection.Next:
            return handleNextSelection(context, shouldConsiderHeaders);
        case SelectionDirection.Previous:
            return handlePreviousSelection(context, shouldConsiderHeaders);
        case SelectionDirection.NextPage:
            return handleNextPageSelection(context);
        case SelectionDirection.PreviousPage:
            return handlePreviousPageSelection(context);
        default:
            return { selectedIndex: -1, headerToFocus: null };
    }
}

/**
 * Handles next row selection (down arrow)
 */
function handleNextSelection(
    context: NavigationContext,
    shouldConsiderHeaders: boolean
): NavigationResult {
    const { tableView, currentSelectedIndex, totalRowsCount } = context;
    let selectedIndex = currentSelectedIndex;
    let headerToFocus: string | null = null;

    if (shouldConsiderHeaders && tableView.headerToFocus && !isImmersiveReadingPaneShown()) {
        const currentHeader = virtualizedMailGroupHeadersStore().virtualizedGroupHeaders.get(
            tableView.headerToFocus
        );
        const isCurrentHeaderCollapsed = currentHeader?.isCollapsed ?? false;

        if (currentHeader && isCurrentHeaderCollapsed) {
            // If the header is collapsed, select the first row that the header represents
            // to be used as an anchor and select the header to focus
            const lastRowKey = currentHeader.rowKeys[currentHeader.rowKeys.length - 1];
            const lastRowIndex = tableView.rowKeys.indexOf(lastRowKey);

            if (lastRowIndex === totalRowsCount - 1) {
                headerToFocus = tableView.headerToFocus;
            } else if (lastRowIndex !== -1) {
                selectedIndex = lastRowIndex + 1;
                headerToFocus = getActionableHeader(selectedIndex, tableView);
            }
        }
        // If the current header is not collapsed, keep headerToFocus as null to focus on the next row
    } else if (selectedIndex < totalRowsCount - 1) {
        // if the header is not focused, select the next row and focus on its header if applicable
        selectedIndex++;
        if (shouldConsiderHeaders && !isImmersiveReadingPaneShown()) {
            headerToFocus = getActionableHeader(selectedIndex, tableView);
        }
    }

    return { selectedIndex, headerToFocus };
}

/**
 * Handles previous row selection (up arrow)
 */
function handlePreviousSelection(
    context: NavigationContext,
    shouldConsiderHeaders: boolean
): NavigationResult {
    const { tableView, currentSelectedIndex } = context;
    let selectedIndex = currentSelectedIndex;
    let headerToFocus: string | null = null;

    if (shouldConsiderHeaders && !isImmersiveReadingPaneShown()) {
        if (!tableView.headerToFocus) {
            // if no header is focused and the current selection has a header, focus on it
            headerToFocus = getActionableHeader(selectedIndex, tableView);
            if (headerToFocus) {
                return { selectedIndex, headerToFocus };
            }
        } else {
            const currentHeader = virtualizedMailGroupHeadersStore().virtualizedGroupHeaders.get(
                tableView.headerToFocus
            );
            const firstRowKey = currentHeader?.rowKeys[0];
            const firstRowIndex = firstRowKey ? tableView.rowKeys.indexOf(firstRowKey) : -1;

            if (selectedIndex === 0 || (currentHeader?.isCollapsed && firstRowIndex === 0)) {
                // If already at the table's top edge, keep the header focus
                headerToFocus = tableView.headerToFocus;
                return { selectedIndex, headerToFocus };
            }
        }
    }

    if (selectedIndex > 0) {
        const headerText = getParentGroupHeader(tableView.rowKeys[selectedIndex - 1], tableView);

        const previousHeader = headerText
            ? virtualizedMailGroupHeadersStore().virtualizedGroupHeaders.get(headerText)
            : undefined;
        const isPreviousHeaderCollapsed = previousHeader?.isCollapsed ?? false;

        if (
            shouldConsiderHeaders &&
            previousHeader &&
            isPreviousHeaderCollapsed &&
            !isImmersiveReadingPaneShown()
        ) {
            // If the header is collapsed, select the first row that the header represents
            // to be used as an anchor and select the header to focus
            const firstRowKey = previousHeader.rowKeys[0];
            const firstRowIndex = tableView.rowKeys.indexOf(firstRowKey);

            if (firstRowIndex !== -1) {
                selectedIndex = firstRowIndex;
                headerToFocus = getActionableHeader(selectedIndex, tableView);
            }
        } else {
            // If the header is not collapsed, select the row above the current selection
            selectedIndex--;
            headerToFocus = null;
        }
    }

    return { selectedIndex, headerToFocus };
}

/**
 * Handles next page selection (Page Down)
 */
function handleNextPageSelection(context: NavigationContext): NavigationResult {
    const { currentSelectedIndex, visibleEndIndex, numVisibleRows, totalRowsCount } = context;
    let selectedIndex = currentSelectedIndex;

    // If not at the last visible index, jump to the last visible index
    if (selectedIndex !== visibleEndIndex) {
        selectedIndex = Math.min(visibleEndIndex, totalRowsCount - 1);
    } else {
        // If at the last visible index, jump by one page worth of rows
        const newIndex = selectedIndex + numVisibleRows;
        selectedIndex = Math.min(newIndex, totalRowsCount - 1);
    }

    return { selectedIndex, headerToFocus: null };
}

/**
 * Handles previous page selection (Page Up)
 */
function handlePreviousPageSelection(context: NavigationContext): NavigationResult {
    const { currentSelectedIndex, visibleStartIndex, numVisibleRows } = context;
    let selectedIndex = currentSelectedIndex;

    // If not at the first visible index, jump to the first visible index
    if (selectedIndex !== visibleStartIndex) {
        selectedIndex = visibleStartIndex;
    } else {
        // If at the first visible index, jump by one page worth of rows
        const newIndex = selectedIndex - numVisibleRows;
        selectedIndex = Math.max(newIndex, 0);
    }

    return { selectedIndex, headerToFocus: null };
}

function getActionableHeader(rowIndex: number, tableView: TableView): string | null {
    // Validate row index bounds
    if (rowIndex < 0 || rowIndex >= tableView.rowKeys.length) {
        return null;
    }

    const selectedRowKey = tableView.rowKeys[rowIndex];
    const previousRowKey = rowIndex > 0 ? tableView.rowKeys[rowIndex - 1] : null;
    const headerGenerator = getMailListGroupHeaderGenerator(tableView);

    if (!headerGenerator) {
        return null;
    }

    const header = getMailListGroupHeader(
        previousRowKey,
        selectedRowKey,
        tableView,
        headerGenerator
    );

    if (!header) {
        return null;
    }

    // Check if all row keys for the header are loaded
    const virtualizedHeaders = virtualizedMailGroupHeadersStore().virtualizedGroupHeaders;
    const headerData = virtualizedHeaders.get(header);
    const isLoadingComplete = headerData?.isLoadingOfRowKeysComplete ?? false;

    return isLoadingComplete ? header : null;
}
